import { ColDef, ColGroupDef } from "@ag-grid-community/core";
import { Badge } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { clsx } from "clsx";
import {
  hypotheticalWaterfallRetrieve,
  useHypotheticalWaterfallCapitalActivityDataRetrieve,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import {
  useHypotheticalWaterfallCategoriesRetrieveSuspense,
  useHypotheticalWaterfallCreate,
} from "gen/endpoints/hypothetical-waterfall/hypothetical-waterfall";
import { useEndOfPeriodReportDateDetailsRetrieveSuspense } from "gen/endpoints/next-end-of-period-report/next-end-of-period-report";
import { HypotheticalTransactionCodeData } from "gen/model/hypotheticalTransactionCodeData";
import { HypotheticalWaterfallData } from "gen/model/hypotheticalWaterfallData";
import { HypotheticalWaterfallEntityCreateData } from "gen/model/hypotheticalWaterfallEntityCreateData";
import { hypotheticalWaterfallCreateBody } from "gen/schemas/hypothetical-waterfall/hypothetical-waterfall";
import { get, noop } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { MaybernTableBoxActions } from "shared/components/table/maybern/MaybernTableBoxActions";
import { MaybernTableGetErrorForCellFn } from "shared/components/table/maybern/MaybernTableErrors";
import { useMaybernTableFormRowController } from "shared/components/table/maybern/MaybernTableFormRowController";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { getApiErrorFromResponse } from "shared/utils/ErrorUtils";
import { sumMoneyStrings } from "shared/utils/MoneyUtils";

interface HypoWaterfallCreateFormData {
  entity_id_and_transaction_code_id_to_amount: HypoWaterfallCreateFundFormData[];
  start_date: string;
  end_date: string;
}

interface HypoWaterfallCreateFundFormData {
  id: string;
  entity_id: string;
  transaction_code_id_to_amount: Record<string, string>;
}

interface HypoWaterfallDateInformation {
  startDate: string;
  endDate: string;
  dateRangeRichValue: string;
}

interface HypoWaterfallCreateFundRowData
  extends HypoWaterfallCreateFundFormData {
  prior_period_ending_nav: string;
  management_fees: string;
  capital_activity: {
    contributions: string;
    distributions: string;
  };
  legal_name: string;
}

export interface HypotheticalWaterfallInputProps {
  endOfPeriodReportId?: string;
  dateInformation?: HypoWaterfallDateInformation;
  fundIdToTransactionCodeToAmount: { [key: string]: { [key: string]: string } };
}

export const HypotheticalWaterfallInput: React.FC<
  HypotheticalWaterfallInputProps
> = ({
  endOfPeriodReportId,
  fundIdToTransactionCodeToAmount,
  dateInformation,
}) => {
  const { fundFamilyId } = useRequiredStringParams();

  const { t } = useTranslation(["financialReporting"]);
  const navigate = useNavigate();

  const { data: reportData } =
    useEndOfPeriodReportDateDetailsRetrieveSuspense(fundFamilyId);

  const evaluatedDateInformation = dateInformation || {
    startDate: reportData.start_date,
    endDate: reportData.end_date,
    dateRangeRichValue: reportData.period_rich_value,
  };

  const { data: codeData } = useHypotheticalWaterfallCategoriesRetrieveSuspense(
    fundFamilyId,
    { effective_date: nowAsIsoDate() },
  );
  const baseRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .financialReporting({});
  const { data: capitalActivityDataByFundEntity } =
    useHypotheticalWaterfallCapitalActivityDataRetrieve(fundFamilyId, {
      effective_date: evaluatedDateInformation.endDate,
      start_date: evaluatedDateInformation.startDate,
    });
  const [hypotheticalWaterfallData, setHypotheticalWaterfallData] =
    useState<HypotheticalWaterfallData>();

  const {
    mutateAsync: createHypotheticalWaterfall,
    error: createError,
    variables: createVariables,
  } = useHypotheticalWaterfallCreate();

  const onCeleryTaskSuccess = async () => {
    if (!hypotheticalWaterfallData) {
      return;
    }
    // need to refresh this data to get the is_carry_clawback_available flag
    const data = await hypotheticalWaterfallRetrieve(
      fundFamilyId,
      hypotheticalWaterfallData.end_of_period_report_id,
      hypotheticalWaterfallData.id,
    );
    if (data.is_carry_clawback_available) {
      navigate(
        baseRoute
          .hypotheticalWaterfall({
            hypotheticalWaterfallId: hypotheticalWaterfallData.id,
            endOfPeriodReportId:
              hypotheticalWaterfallData.end_of_period_report_id,
          })
          .designateProtectedCarry({}).$,
      );
      return;
    }
    navigate(
      baseRoute
        .hypotheticalWaterfall({
          hypotheticalWaterfallId: hypotheticalWaterfallData.id,
          endOfPeriodReportId:
            hypotheticalWaterfallData.end_of_period_report_id,
        })
        .review({})
        .waterfallDetails({}).$,
    );
  };

  useCeleryWorkflowPoll(
    hypotheticalWaterfallData
      ? [hypotheticalWaterfallData.celery_workflow_id]
      : undefined,
    () => noop,
    onCeleryTaskSuccess,
    onCeleryTaskSuccess,
  );

  const { data: fundList } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    fund_ids: Object.keys(codeData.fund_id_to_allocation_details),
  });

  const columnDefs = useHypoWaterfallCreateColDefs(codeData);

  const form = useForm<HypoWaterfallCreateFormData>({
    resolver: zodResolver(hypotheticalWaterfallCreateBody),
    values: {
      end_date: evaluatedDateInformation.endDate,
      start_date: evaluatedDateInformation.startDate,
      entity_id_and_transaction_code_id_to_amount: fundList.map((fund) => {
        const fundBreakdownData =
          capitalActivityDataByFundEntity?.entity_id_to_breakdown_data[fund.id];

        return {
          id: fund.id,
          entity_id: fund.id,
          legal_name: fund.legal_name,
          transaction_code_id_to_amount:
            fundIdToTransactionCodeToAmount[fund.id],
          prior_period_ending_nav:
            fundBreakdownData?.prior_period_ending_nav ?? "0",
          management_fees: fundBreakdownData?.fees ?? "0",
          capital_activity: {
            contributions: fundBreakdownData?.capital_call_amount ?? "0",
            distributions: fundBreakdownData?.distribution_amount ?? "0",
          },
        };
      }),
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const getErrorForCell = useCallback<
    MaybernTableGetErrorForCellFn<HypoWaterfallCreateFundRowData>
  >(
    (params) => {
      const key = `entity_id_and_transaction_code_id_to_amount.${params.node.rowIndex}.${params.column!.getColId()}`;
      const currentValue = get(form.getValues(), key);
      const submittedValue = get(createVariables?.data, key);
      if (submittedValue !== undefined && currentValue !== submittedValue) {
        return undefined;
      }
      const apiError = getApiErrorFromResponse(createError);
      if (!apiError) {
        return undefined;
      }
      const fieldErrors = get(apiError.data, key) as string[] | undefined;
      return fieldErrors?.join(", ");
    },
    [createError, createVariables, form],
  );

  const { propsForRows } = useMaybernTableFormRowController(
    form,
    "entity_id_and_transaction_code_id_to_amount",
  );
  const isLoading = form.formState.isSubmitting || !!hypotheticalWaterfallData;

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        size="lg"
        title={t("financialReporting:hypoCreate.title")}
        helperText={t("financialReporting:hypoCreate.instructions")}
        badge={<Badge>{evaluatedDateInformation.dateRangeRichValue}</Badge>}
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={async (data) => {
            const entityToTransactionCodes: HypotheticalWaterfallEntityCreateData[] =
              [];
            data.entity_id_and_transaction_code_id_to_amount.forEach((fund) => {
              entityToTransactionCodes.push({
                entity_id: fund.entity_id,
                transaction_code_id_to_amount:
                  fund.transaction_code_id_to_amount,
              });
            });
            const hypotheticalWaterfallResult =
              await createHypotheticalWaterfall({
                fundFamilyId,
                data: {
                  end_date: evaluatedDateInformation.endDate,
                  start_date: evaluatedDateInformation.startDate,
                  end_of_period_report_id: endOfPeriodReportId,
                  entity_id_and_transaction_code_id_to_amount:
                    entityToTransactionCodes,
                },
              });
            setHypotheticalWaterfallData(hypotheticalWaterfallResult);
          }}
          excludeNonFieldErrors={[
            "entity_id_and_transaction_code_id_to_amount",
          ]}
        >
          <TableBox>
            <TableBoxHeader>
              <TableBoxTitle>
                {t("financialReporting:hypoCreate.tableTitle", {
                  quarter: evaluatedDateInformation.dateRangeRichValue,
                })}
              </TableBoxTitle>
              <MaybernTableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <MaybernTable
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getErrorForCell={getErrorForCell}
                {...propsForRows}
              />
            </TableBoxBody>
          </TableBox>
          <FormFooter>
            <FormFooterActions>
              <FormCancelButton
                isDisabled={isLoading}
                onClick={() =>
                  navigate(
                    rootRoute({})
                      .fundFamilies({})
                      .fundFamily({ fundFamilyId })
                      .financialReporting({}).$,
                  )
                }
              />
              <FormSubmitButton isDisabled={isLoading}>
                {t("financialReporting:hypoCreate.submit")}
              </FormSubmitButton>
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageBody>
    </PageContent>
  );
};

const defaultColDef: ColDef = {
  lockPosition: true,
  sortable: false,
};

const useHypoWaterfallCreateColDefs = (
  codeData: HypotheticalTransactionCodeData,
) => {
  const { t } = useTranslation(["financialReporting"]);
  return useMemo(
    (): (
      | ColDef<HypoWaterfallCreateFundRowData>
      | ColGroupDef<HypoWaterfallCreateFundRowData>
    )[] => [
      {
        field: "legal_name",
        headerName: t("financialReporting:hypoCreate.columns.entity.title"),
        cellClassRules: {
          "mb-cell-is-header": () => true,
        },
      },
      {
        colId: "priorNav",
        headerName: t("financialReporting:hypoCreate.columns.priorNav.title"),
        headerClass: clsx({
          "mb-header-has-title": true,
        }),
        children: [
          {
            field: "prior_period_ending_nav",
            headerName: t(
              "financialReporting:hypoCreate.columns.priorNav.title",
            ),
            type: "money",
          },
        ],
      },
      {
        colId: "plGroup",
        headerName: t("financialReporting:hypoCreate.columns.pl.title"),
        headerClass: clsx({
          "mb-header-has-title": true,
        }),
        children: [
          ...codeData.transaction_codes.map((code) => ({
            field:
              `transaction_code_id_to_amount.${code.id}` as "transaction_code_id_to_amount.XYZ",
            headerName: code.name,
            type: "moneyInput",
            initialWidth: 220,
          })),
          {
            field: "management_fees",
            headerName: t(
              "financialReporting:hypoCreate.columns.managementFees.title",
            ),
            type: "money",
          },
        ],
      },
      {
        colId: "capitalActivityGroup",
        headerName: t(
          "financialReporting:hypoCreate.columns.capitalActivity.title",
        ),
        headerClass: clsx({
          "mb-header-has-title": true,
        }),
        children: [
          {
            field: "capital_activity.contributions",
            headerName: t(
              "financialReporting:hypoCreate.columns.contributions.title",
            ),
            type: "money",
          },
          {
            field: "capital_activity.distributions",
            headerName: t(
              "financialReporting:hypoCreate.columns.distributions.title",
            ),
            type: "money",
          },
        ],
      },
      {
        colId: "totalNav",
        headerName: t("financialReporting:hypoCreate.columns.totalNav.title"),
        cellClassRules: {
          "mb-cell-computed": () => true,
        },
        type: "money",
        valueGetter: (params) =>
          params.data
            ? sumMoneyStrings([
                ...Object.values(params.data.transaction_code_id_to_amount),
                params.data.management_fees,
                params.data.capital_activity.contributions,
                params.data.capital_activity.distributions,
                params.data.prior_period_ending_nav,
              ])
            : undefined,
      },
    ],
    [codeData.transaction_codes, t],
  );
};
